// You can write more code here

import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import Player from '@/game/prefabs/World/Player';
import Portal from '@/game/prefabs/World/Portal';
import { NodeStatus } from '@/game/prefabs/World/WorldNode';
import TilemapScene from '@/game/scenes/TilemapScenes/TilemapScene';
import _ from 'lodash';
import NPCGuidanceArrow from './NPCGuidanceArrow';

/* START OF COMPILED CODE */

/* START-USER-IMPORTS */
/* END-USER-IMPORTS */

export default class GuidancePanel extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    // arrowOrigin
    const arrowOrigin = scene.add.container(0, 0);
    this.add(arrowOrigin);

    // arrowHolder
    const arrowHolder = scene.add.container(0, 0);
    arrowOrigin.add(arrowHolder);

    // guidanceArrow
    const guidanceArrow = scene.add.image(0, 0, 'GuidanceArrow-purple');
    guidanceArrow.setOrigin(0.5, 1);
    arrowHolder.add(guidanceArrow);

    // arrowGlow
    const arrowGlow = scene.add.image(0, 0, 'GuidanceArrowGlow');
    arrowGlow.setOrigin(0.5, 1);
    arrowGlow.alpha = 0.8;
    arrowGlow.alphaTopLeft = 0.8;
    arrowGlow.alphaTopRight = 0.8;
    arrowGlow.alphaBottomLeft = 0.8;
    arrowGlow.alphaBottomRight = 0.8;
    arrowHolder.add(arrowGlow);

    // npcGuidanceContainer
    const npcGuidanceContainer = scene.add.container(0, 0);
    this.add(npcGuidanceContainer);

    this.guidanceArrow = guidanceArrow;
    this.arrowGlow = arrowGlow;
    this.arrowHolder = arrowHolder;
    this.npcGuidanceContainer = npcGuidanceContainer;

    /* START-USER-CTR-CODE */
    scene.events.on('render', this.updateGuidanceArrow, this);
    scene.events.on('render', this.updateNPCGuidanceArrows, this);
    EventsManager.registerEventOnce(
      this.scene,
      GAME_EVENTS.TILEMAP_LEVEL_INITIALIZED,
      this.handleTilemapLevelInitialized,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.DUEL_STARTED,
      this.handleDuelStarted,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.DUEL_ENDED,
      this.handleDuelEnded,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.MINIGAME_STARTED,
      this.handleMinigameStarted,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.MINIGAME_ENDED,
      this.handleMinigameEnded,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.PLAYER_REQUESTED_NPC_GUIDANCE,
      this.handleGuidanceRequested,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.INVENTORY_OPENED,
      this.handleInventoryOpened,
      this,
    );
    EventsManager.registerEventOn(
      this.scene,
      GAME_EVENTS.INVENTORY_CLOSED,
      this.handleInventoryClosed,
      this,
    );
    this.arrowHolder.setVisible(false);
    /* END-USER-CTR-CODE */
  }

  private guidanceArrow: Phaser.GameObjects.Image;
  private arrowGlow: Phaser.GameObjects.Image;
  private arrowHolder: Phaser.GameObjects.Container;
  private npcGuidanceContainer: Phaser.GameObjects.Container;

  /* START-USER-CODE */
  private portal?: Portal;
  private camera?: Phaser.Cameras.Scene2D.BaseCamera;
  private player?: Player;
  private tilemapScene?: TilemapScene;
  private npcGuidanceActive: boolean = false;
  private npcGuidanceArrows: NPCGuidanceArrow[] = [];

  handleTilemapLevelInitialized(data: any) {
    this.portal = data.portal;
    this.camera = data.camera;
    this.player = data.player;
    this.tilemapScene = data.tilemapScene;

    // Back and forth tween
    this.scene.tweens.add({
      targets: [this.guidanceArrow, this.arrowGlow],
      y: 10,
      scale: 0.9,
      ease: 'Ease.inOut',
      repeat: -1,
      yoyo: true,
      duration: 1000,
    });

    this.scene.tweens.add({
      targets: this.arrowGlow,
      alpha: 0,
      ease: 'Ease.inOut',
      repeat: -1,
      yoyo: true,
      duration: 1000,
    });
  }

  handleDuelStarted() {
    this.setVisible(false);
  }

  handleDuelEnded() {
    this.setVisible(true);
  }

  handleMinigameStarted() {
    this.setVisible(false);
  }

  handleMinigameEnded() {
    this.setVisible(true);
  }

  handleGuidanceRequested() {
    this.setNPCGuidanceActive(!this.npcGuidanceActive);
  }

  handleInventoryOpened() {
    this.setVisible(false);
  }

  handleInventoryClosed() {
    this.setVisible(true);
  }

  setNPCGuidanceActive(active: boolean) {
    this.npcGuidanceActive = active;

    if (active) {
      if (!this.tilemapScene) return;
      for (let i = 0; i < this.tilemapScene?.nodes.length; i++) {
        if (!this.tilemapScene.nodes[i].visible) continue;
        const newNPCGuidanceArrow = new NPCGuidanceArrow(
          this.scene,
          i * 100 + 100,
          150,
        );
        this.scene.add.existing(newNPCGuidanceArrow);
        this.npcGuidanceContainer.add(newNPCGuidanceArrow);
        this.npcGuidanceArrows.push(newNPCGuidanceArrow);
      }
    } else {
      for (let i = 0; i < this.npcGuidanceArrows.length; i++) {
        this.npcGuidanceArrows[i].destroy();
      }
      this.npcGuidanceArrows = [];
    }
  }

  updateNPCGuidanceArrows() {
    if (!this.npcGuidanceActive) return;
    const { camera, player, scene } = this;
    this.npcGuidanceArrows.forEach((arrow, idx) => {
      if (!arrow || !camera || !player) return;
      const node = this.tilemapScene?.nodes[idx];
      if (!node) return;

      const { x, y } = node;

      const npcOffsetY = -32;

      // Get the intended position of the arrows.
      const cameraCenterX =
        (x + node.nPC.x - camera.midPoint.x) * camera.zoom + camera.width / 2;
      const cameraCenterY =
        (y + node.nPC.y - camera.midPoint.y) * camera.zoom +
        camera.height / 2 +
        npcOffsetY;

      // Clamp to within screen coordinates.
      let arrowScreenPosX = _.clamp(cameraCenterX, 32, camera.width - 32);
      let arrowScreenPosY = _.clamp(cameraCenterY, 32, camera.height - 32);

      arrow.setPosition(arrowScreenPosX, arrowScreenPosY);

      // Rotate the arrow to point to the node.
      let angle = Phaser.Math.Angle.Between(
        player.x,
        player.y,
        x + node.nPC.x,
        y + node.nPC.y,
      );

      const npcInView =
        x + node.nPC.x > camera.worldView.x &&
        x + node.nPC.x < camera.worldView.x + camera.worldView.width &&
        y + node.nPC.y > camera.worldView.y &&
        y + node.nPC.y < camera.worldView.y + camera.worldView.height;

      if (npcInView) {
        arrow.setVisible(false);
      } else {
        arrow.setVisible(true);
        const addedDegrees = -90;
        angle = angle + Phaser.Math.DegToRad(addedDegrees);
        let angleDegrees = Phaser.Math.RadToDeg(angle);
        if (angleDegrees < -180) {
          let remainder = angleDegrees % 180;
          angle = Phaser.Math.DegToRad(remainder + 180);
          arrow.setRotation(angle);
        }

        scene.tweens.add({
          targets: arrow,
          rotation: angle,
          duration: 30,
        });
      }

      if (node.npcStatus === NodeStatus.COMPLETE) {
        arrow.setVisible(false);
      }
    });
  }

  updateGuidanceArrow() {
    if (!this.portal || !this.camera || !this.player) return;
    const { camera, portal, arrowHolder, player, scene } = this;

    if (!portal.visible) {
      arrowHolder.setVisible(false);
      return;
    }
    arrowHolder.setVisible(true);

    function getPortalScreenPos() {
      const camTopLeft = { x: camera.worldView.left, y: camera.worldView.top };
      const portalPos = {
        x: portal.getWorldTransformMatrix().tx,
        y: portal.getWorldTransformMatrix().ty,
      };
      return {
        x: (portalPos.x - camTopLeft.x) * camera.zoom,
        y: (portalPos.y - camTopLeft.y) * camera.zoom,
      };
    }

    function portalIsInView() {
      return (
        portal.x > camera.worldView.x &&
        portal.x < camera.worldView.x + camera.worldView.width &&
        portal.y > camera.worldView.y &&
        portal.y < camera.worldView.y + camera.worldView.height
      );
    }

    function rotateGuidanceArrow() {
      let angle = Phaser.Math.Angle.Between(
        player.x,
        player.y,
        portal.x,
        portal.y,
      );

      const addedDegrees = -90;
      angle = angle + Phaser.Math.DegToRad(addedDegrees);
      let angleDegrees = Phaser.Math.RadToDeg(angle);
      if (angleDegrees < -180) {
        let remainder = angleDegrees % 180;
        angle = Phaser.Math.DegToRad(remainder + 180);
        arrowHolder.setRotation(angle);
      }

      scene.tweens.add({
        targets: arrowHolder,
        rotation: angle,
        duration: 30,
      });
      return angle;
    }

    let portalScreenPos = getPortalScreenPos();

    // Clamp portal screen pos.
    portalScreenPos.x = _.clamp(
      portalScreenPos.x,
      32,
      camera.scene.scale.width - 32,
    );
    portalScreenPos.y = _.clamp(
      portalScreenPos.y,
      32,
      camera.scene.scale.height - 32,
    );

    this.arrowHolder.setPosition(portalScreenPos.x, portalScreenPos.y);

    if (portalIsInView()) {
      this.scene.tweens.add({
        targets: arrowHolder,
        rotation: 0,
        duration: 30,
      });
    } else {
      rotateGuidanceArrow();
    }
  }

  preDestroy() {
    const { scene } = this;
    scene.events.off('render', this.updateGuidanceArrow, this);
    scene.events.off('render', this.updateGuidanceArrow, this);
    scene.events.off('render', this.updateNPCGuidanceArrows, this);

    this.tilemapScene = undefined;
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
